import { useSelector } from "react-redux";

import { FilterSpecification, Map } from "maplibre-gl";
import { FeatureCollection, Geometry } from "geojson";

import {
  CATCHMENT_ID_ATTRIBUTE,
  GRID_ID_ATTRIBUTE
} from "@/components/analysisSummary/filterByGrid";
import { SUBLAYERS } from "@/constants/map";
import { RootState } from "@/store";
import { LegendFilterType } from "@/store/slice/mapSlice";
import { SpatialAggregationType } from "@/types/common";
import { Dataset, DatasetData } from "@/types/dataset";
import { LayerType, SelectedFeature } from "@/types/map";
import {
  convertDatasetResponseToFeatureCollection,
  hasCustomCategoryAssignments
} from "@/utils/dataset";
import {
  filterPointFeatures,
  filterPointFeaturesCustomCategory,
  filterPointFeaturesCustomRange,
  generateCustomCategoryLegendFilter,
  generateCustomRangeLegendFilter,
  generateNonPointMapFilter,
  isCustomCategoryFeatureHidden,
  isCustomRangeFeatureHidden,
  isFeatureMatchingFilters,
  LayerSublayers,
  NON_POI_LAYER_TYPES
} from "@/utils/map";

type useLegendFilterProps = {
  map: Map;
  handleAddPointsLayer: (
    dataset: Dataset,
    pointsData: FeatureCollection<Geometry>,
    customId?: string,
    filteredData?: FeatureCollection<Geometry>,
    beforeId?: string
  ) => void;
  handleRemoveSourceLayer: (
    datasetId: string,
    spatialType: SpatialAggregationType
  ) => void;
  handleClearSelectedState: (currentSelectedFeature: SelectedFeature) => void;
};

export const useLegendFilter = ({
  map,
  handleAddPointsLayer,
  handleRemoveSourceLayer,
  handleClearSelectedState
}: useLegendFilterProps) => {
  const { selectedFeature, timeSeriesGroupByFilter } = useSelector(
    (state: RootState) => state.map
  );

  const handleFilterLayerByLegend = (
    newfilterLegends: LegendFilterType[],
    dataset: Dataset,
    rawDataset: DatasetData,
    beforeId?: string
  ) => {
    if (!map) return;
    const filters = newfilterLegends ?? [];
    const spatialType = dataset.properties.spatialType;
    const layers: LayerSublayers = SUBLAYERS(dataset.data.id)[spatialType];

    // Get attribute name - use stroke attribute for line datasets
    const field =
      spatialType === LayerType.LINE
        ? (dataset.style.stroke?.attribute_name ??
          dataset.selectedCategory.attribute_name)
        : dataset.selectedCategory.attribute_name;

    // Get data type - look up from categories for line datasets
    const dataType =
      spatialType === LayerType.LINE
        ? (dataset.categories.find((c) => c.attribute_name === field)
            ?.data_type ?? dataset.selectedCategory.data_type)
        : dataset.selectedCategory.data_type;

    const colorRanges = dataset.style.colorRanges;

    const nonNullRanges = colorRanges.filter(
      (r) => r.value !== "" && r.label?.toLowerCase() !== "null"
    );

    const top9Values = nonNullRanges
      .slice(0, -1)
      .map((r) => r.value)
      .filter((v): v is string => typeof v === "string");

    // Custom Ordinal toggles hide *categories* (sets of assigned values) rather
    // than single ordinal values, so it uses its own value-set based filtering —
    // but only once values are assigned; otherwise it renders/filters like the
    // default ordinal scale.
    const customCategoryConfig = dataset.style.customCategoryConfig;
    const isCustomCategory =
      dataset.style.colorScaleMethod === "custom_ordinal" &&
      hasCustomCategoryAssignments(customCategoryConfig);

    // Custom Breaks toggles hide numeric *ranges* (and the "Other" gap bucket).
    const customRangeConfig = dataset.style.customRangeConfig;
    const isCustomRange =
      dataset.style.colorScaleMethod === "custom_breaks" &&
      !!customRangeConfig?.steps.length;

    if (spatialType === LayerType.POINT) {
      handleRemoveSourceLayer(dataset.data.id, "poi");

      // Find column indices
      const gridIndex = dataset.categories.findIndex(
        (attr) =>
          attr.attribute_name.toLowerCase() === GRID_ID_ATTRIBUTE.toLowerCase()
      );
      const catchmentIndex = dataset.categories.findIndex(
        (attr) =>
          attr.attribute_name.toLowerCase() ===
          CATCHMENT_ID_ATTRIBUTE.toLowerCase()
      );

      // If there is no `Grid ID` or `Catchment ID` attribute in the dataset, use the dataset categories
      const attributeSchemas =
        gridIndex === -1 && catchmentIndex === -1
          ? dataset.categories
          : undefined;

      const originalFeatureCollection =
        convertDatasetResponseToFeatureCollection(rawDataset, attributeSchemas);

      let filteredFeatureCollection: FeatureCollection<Geometry> | undefined =
        undefined;

      if (filters.length > 0) {
        let filteredFeatures;
        if (isCustomCategory && customCategoryConfig) {
          filteredFeatures = filterPointFeaturesCustomCategory(
            originalFeatureCollection.features,
            field,
            customCategoryConfig,
            filters
          );
        } else if (isCustomRange && customRangeConfig) {
          filteredFeatures = filterPointFeaturesCustomRange(
            originalFeatureCollection.features,
            field,
            customRangeConfig,
            filters
          );
        } else {
          filteredFeatures = filterPointFeatures(
            originalFeatureCollection.features,
            field,
            filters,
            dataType,
            top9Values
          );
        }

        filteredFeatureCollection = {
          ...originalFeatureCollection,
          features: filteredFeatures
        };
      }

      handleAddPointsLayer(
        dataset,
        originalFeatureCollection,
        undefined,
        filteredFeatureCollection,
        beforeId
      );
    } else {
      // For non-point layers, apply filter to fill and outline layers
      let legendFilter;
      if (isCustomCategory && customCategoryConfig) {
        legendFilter = generateCustomCategoryLegendFilter(
          field,
          customCategoryConfig,
          filters
        );
      } else if (isCustomRange && customRangeConfig) {
        legendFilter = generateCustomRangeLegendFilter(
          field,
          customRangeConfig,
          filters
        );
      } else {
        legendFilter = generateNonPointMapFilter(
          field,
          filters,
          dataType,
          top9Values
        );
      }

      // Compose with the current time-series filter when this dataset is a
      // timeseries (group_by set) and a frame is active. Both filters share the
      // same setFilter slot on the layer, so the two must be merged into a
      // single `["all", ...]` expression — otherwise one overwrites the other.
      const tsFilter =
        dataset.group_by &&
        dataset.group_by !== "none" &&
        timeSeriesGroupByFilter
          ? timeSeriesGroupByFilter[dataset.data.id]
          : undefined;
      let timeFilter: unknown = null;
      if (tsFilter?.time !== undefined && tsFilter.attribute) {
        const isNumber =
          tsFilter.time !== "" && !Number.isNaN(Number(tsFilter.time));
        const parsedTime = isNumber ? Number(tsFilter.time) : tsFilter.time;
        timeFilter = ["==", ["get", tsFilter.attribute], parsedTime];
      }

      let composedFilter: FilterSpecification | null = null;
      if (legendFilter && timeFilter) {
        composedFilter = [
          "all",
          legendFilter,
          timeFilter
        ] as unknown as FilterSpecification;
      } else if (legendFilter) {
        composedFilter = legendFilter as unknown as FilterSpecification;
      } else if (timeFilter) {
        composedFilter = timeFilter as FilterSpecification;
      }

      // Update all non-point layers with the new filter
      NON_POI_LAYER_TYPES.forEach((layerKey) => {
        const layerId = layers[layerKey];
        if (layerId) map.setFilter(layerId, composedFilter);
      });
    }

    if (selectedFeature) {
      const value = selectedFeature.properties?.[field];
      let isHidden;
      if (isCustomCategory && customCategoryConfig) {
        isHidden = isCustomCategoryFeatureHidden(
          value,
          customCategoryConfig,
          filters
        );
      } else if (isCustomRange && customRangeConfig) {
        isHidden = isCustomRangeFeatureHidden(
          value,
          customRangeConfig,
          filters
        );
      } else {
        isHidden = isFeatureMatchingFilters(
          value,
          filters,
          dataType,
          top9Values
        );
      }
      if (isHidden && selectedFeature.source === dataset.data.id) {
        handleClearSelectedState(selectedFeature);
      }
    }
  };

  return {
    handleFilterLayerByLegend
  };
};

export default useLegendFilter;
